/**
 * How a trust score is coloured, in one place.
 *
 * Colour is a function of the BAND, never of the number. Two band scales exist and they are not
 * interchangeable: the Trust Score's are fixed and published (85 / 70 / 50 / 30, versioned in
 * the methodology), while the runtime autonomy thresholds are the customer's to set per org,
 * tenant or agent. Whoever owns the thresholds resolves the band; this module only says what a
 * band looks like. That split is why the two can never drift into each other.
 *
 * Every value is a CSS custom property rather than a hex literal, so a dial follows the active
 * theme. A hardcoded `#16a34a` stays light-mode green on a dark background.
 */

/**
 * A score as a number, or {@code null} when there is no score.
 *
 * Exists because {@code Number('')} is {@code 0} and {@code Number(' ')} is {@code 0}. An empty
 * string is what an API field carries when nothing has been computed yet, and passing it through a
 * finiteness check lands it on zero — which bands as the worst possible result. The distinction
 * between "not measured" and "measured and bad" is the one thing every surface here must keep.
 */
function toScore(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  if (typeof value === 'boolean') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The published passport bands, in descending order. Fixed — not configurable by anyone. */
export const PUBLISHED_BANDS = Object.freeze([
  { key: 'strong',   label: 'Strong',   min: 85 },
  { key: 'adequate', label: 'Adequate', min: 70 },
  { key: 'limited',  label: 'Limited',  min: 50 },
  { key: 'weak',     label: 'Weak',     min: 30 },
  { key: 'critical', label: 'Critical', min: 0 },
]);

/** The absence of a score. Not a band: nothing has been found wrong, nothing has been measured. */
export const UNRATED = 'unrated';

/**
 * The band a passport score falls in.
 *
 * `null`, `undefined` and anything non-numeric return {@link UNRATED} rather than falling through
 * to `critical`. A system nobody has scored must never be coloured as a system that scored badly.
 */
export function publishedBand(score) {
  const n = toScore(score);
  if (n === null) return UNRATED;
  const clamped = Math.max(0, Math.min(100, n));
  return (PUBLISHED_BANDS.find((b) => clamped >= b.min) || PUBLISHED_BANDS[PUBLISHED_BANDS.length - 1]).key;
}

/** The arc / fill colour for a band, as a CSS variable reference. */
export function bandColor(band) {
  return `var(--v2-band-${normalise(band)})`;
}

/** The darker step, for text on a light surface where the arc colour lacks contrast. */
export function bandInk(band) {
  return `var(--v2-band-${normalise(band)}-ink)`;
}

/** The operator-facing label. Unrated says what it is rather than leaving a gap. */
export function bandLabel(band) {
  const key = normalise(band);
  if (key === UNRATED) return 'Not yet scored';
  const found = PUBLISHED_BANDS.find((b) => b.key === key);
  return found ? found.label : 'Not yet scored';
}

/** The soft fill for a band, for a pill or tile sitting behind band-coloured type. */
export function bandSoft(band) {
  return `var(--v2-band-${normalise(band)}-soft)`;
}

/**
 * A band in the shape the existing `ACCENT` palettes use — {@code { bg, text, border, icon }} — so
 * a surface already built around that shape swaps one expression and keeps its markup.
 *
 * Every value is a theme variable, which is the point: the literals this replaces stayed
 * light-mode green on a dark background.
 */
export function bandAccent(band) {
  return {
    bg: bandSoft(band),
    text: bandInk(band),
    border: bandColor(band),
    icon: bandInk(band),
  };
}

/**
 * The number a surface shows for a score: whole, rounded DOWN, clamped to 0..100.
 *
 * Rounding down rather than to nearest, because a score is a claim about a system somebody will
 * act on, and 84.9 presented as 85 is a claim the evidence did not support. The floor used to live
 * only in the backend's design-time engine, which meant the rule held for exactly one caller and
 * silently stopped holding when a second one arrived with a two-decimal runtime score — the dial
 * then rendered "77.91" into a ring sized for two digits. It belongs here, next to the bands, so
 * that the number shown and the band shown are decided in one place by one rule.
 *
 * Returns null for anything unmeasured, which a dial renders as an em dash rather than a number.
 */
export function displayScore(score) {
  const n = toScore(score);
  if (n === null) return null;
  return Math.floor(Math.max(0, Math.min(100, n)));
}

/**
 * Band, colour, ink and label for one score — what a dial needs, resolved once.
 *
 * Banded on the DISPLAYED number, not the raw one, so the label beside a dial is always the band
 * the number on it resolves to. The two agree today because every threshold is an integer, but a
 * surface should not depend on that staying true.
 */
export function publishedBandStyle(score) {
  const shown = displayScore(score);
  if (shown !== null) score = shown;
  const band = publishedBand(score);
  return { band, color: bandColor(band), ink: bandInk(band), label: bandLabel(band),
           soft: bandSoft(band), accent: bandAccent(band) };
}

/**
 * The semantic tone a score reads as — for components that take 'success' | 'warning' | 'danger'
 * rather than a colour.
 *
 * Resolved from the published band so a sparkline, a chip and a dial never disagree. Six surfaces
 * each carried their own thresholds for this, and no two of the sets matched.
 */
export function bandTone(score) {
  return bandToneFor(publishedBand(score));
}

/**
 * The same tone, for a surface that already holds a band rather than a number.
 *
 * A KPI tile counting the agents in a band has no score to pass — it used to reach for a literal
 * instead, which is how the Trust Center ended up colouring its "Adequate" tile amber while every
 * dial beside it drew the same band green.
 */
export function bandToneFor(band) {
  switch (normalise(band)) {
    case 'strong':
    case 'adequate': return 'success';
    case 'limited':  return 'warning';
    case 'weak':
    case 'critical': return 'danger';
    default:         return 'muted';
  }
}

/**
 * The inclusive lower bound of a band, for a surface that has to say where a band starts.
 *
 * Returns the number from the one band table rather than letting a label hardcode it, so a caption
 * that reads "(85+)" cannot survive a change to where Strong begins.
 */
export function bandMin(band) {
  const found = PUBLISHED_BANDS.find((b) => b.key === normalise(band));
  return found ? found.min : null;
}

/**
 * The autonomy thresholds as shipped. A customer may move any of them per org, tenant or agent —
 * these are what to assume when a surface has not been handed the configured set.
 *
 * Mirrors TrustScoringConfig.selfAutonomousThreshold / supervisedThreshold / restrictedThreshold.
 */
export const DEFAULT_AUTONOMY_THRESHOLDS = Object.freeze({
  selfAutonomous: 98,
  supervised: 90,
  restricted: 75,
});

/**
 * The band a RUNTIME trust score falls in — a different question from {@link publishedBand}.
 *
 * The passport's bands are fixed and published. These are the autonomy thresholds, which a customer
 * sets, so they arrive as an argument rather than being baked in here. Pass the configured set
 * whenever the surface has it; the shipped defaults are the fallback, not the rule.
 *
 * Four levels, mapped onto the shared ramp so one score reads the same colour wherever it appears:
 * self-autonomous, supervised and restricted take the top three steps, and below restricted takes
 * critical — an agent on probation is the one state the ladder treats as a failure.
 */
export function autonomyBand(score, thresholds) {
  const n = toScore(score);
  if (n === null) return UNRATED;

  const t = { ...DEFAULT_AUTONOMY_THRESHOLDS, ...(thresholds || {}) };
  const at = (key) => {
    const v = Number(t[key]);
    return Number.isFinite(v) ? v : DEFAULT_AUTONOMY_THRESHOLDS[key];
  };

  const clamped = Math.max(0, Math.min(100, n));
  if (clamped >= at('selfAutonomous')) return 'strong';
  if (clamped >= at('supervised')) return 'adequate';
  if (clamped >= at('restricted')) return 'limited';
  return 'critical';
}

/**
 * The band a COMPLIANCE COVERAGE score falls in — the share of a framework's controls that are
 * assigned and passing.
 *
 * Its own thresholds, deliberately. This is not an agent's conduct and not its autonomy: it is how
 * much of a framework is covered, where 80% is a different kind of claim from an agent scoring 80.
 * Sharing the ramp keeps the colours consistent; sharing the thresholds would quietly restate what
 * the number means.
 */
export const COMPLIANCE_COVERAGE_THRESHOLDS = Object.freeze({ adequate: 80, limited: 60 });

export function complianceCoverageBand(score) {
  const n = toScore(score);
  if (n === null) return UNRATED;
  const clamped = Math.max(0, Math.min(100, n));
  if (clamped >= COMPLIANCE_COVERAGE_THRESHOLDS.adequate) return 'strong';
  if (clamped >= COMPLIANCE_COVERAGE_THRESHOLDS.limited) return 'limited';
  return 'critical';
}

/** Band, colour, ink, soft fill, accent and label for a runtime trust score. */
export function autonomyBandStyle(score, thresholds) {
  const band = autonomyBand(score, thresholds);
  return { band, color: bandColor(band), ink: bandInk(band), label: bandLabel(band),
           soft: bandSoft(band), accent: bandAccent(band) };
}

/** Band, colour, ink, soft fill, accent and label for a compliance coverage score. */
export function complianceCoverageStyle(score) {
  const band = complianceCoverageBand(score);
  return { band, color: bandColor(band), ink: bandInk(band), label: bandLabel(band),
           soft: bandSoft(band), accent: bandAccent(band) };
}


/** An unknown band name is unrated, never the worst band. */
function normalise(band) {
  const key = typeof band === 'string' ? band.trim().toLowerCase() : '';
  if (key === UNRATED) return UNRATED;
  return PUBLISHED_BANDS.some((b) => b.key === key) ? key : UNRATED;
}
